import { Flex, Input, Table, TableContainer, Tbody, Td, Th, Thead, Tr, useDisclosure } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { TableSkeletonRows } from '../../../components/Loading'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { ApiErrorShape, useAppToast } from '../../../helpers'
import { ILeadSource, LeadSourceService } from '../../../services/LeadSource/LeadSourceService'

interface IProps {
  isOpen: boolean
  onClose: () => void
}

/** Adiciona e exclui as opções do campo "Origem" do registro manual de leads. */
export function LeadSourcesModal({ isOpen, onClose }: IProps) {
  const service = new LeadSourceService()
  const toast = useAppToast()
  const [sources, setSources] = useState<ILeadSource[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState<string | string[]>()
  const [isAdding, setIsAdding] = useState(false)
  const [sourceToDelete, setSourceToDelete] = useState<ILeadSource>()
  const deleteDialog = useDisclosure()

  const loadSources = async () => {
    setIsLoading(true)
    setLoadError(false)

    try {
      setSources(await service.list())
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar as origens', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return

    setName('')
    setNameError(undefined)
    loadSources()
  }, [isOpen])

  const handleAdd = async () => {
    if (!name.trim()) {
      setNameError('Informe o nome da origem.')
      return
    }

    setIsAdding(true)
    setNameError(undefined)

    try {
      await service.create(name.trim())
      toast.success('Origem adicionada')
      setName('')
      loadSources()
    } catch (error) {
      const { status, data } = (error as ApiErrorShape).response ?? {}
      const messages = data?.errors?.name

      if ((status === 400 || status === 422) && messages) {
        setNameError(messages)
      } else {
        toast.error('Erro ao adicionar origem', error)
      }
    } finally {
      setIsAdding(false)
    }
  }

  const handleDelete = async () => {
    if (!sourceToDelete) return

    try {
      await service.delete(sourceToDelete.id)
      toast.success('Origem excluída')
      deleteDialog.onClose()
      loadSources()
    } catch (error) {
      toast.error('Erro ao excluir origem', error)
    }
  }

  return (
    <>
      <FormModal isOpen={isOpen} onClose={onClose} title="Gerenciar origens" size="compact">
        <FormField label="Nova origem" error={nameError}>
          <Flex gap={2}>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setNameError(undefined)
              }}
              // Este modal não tem `<form>` (é aberto de dentro de outro formulário), então o Enter é tratado aqui.
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return
                e.preventDefault()
                handleAdd()
              }}
              isDisabled={isAdding}
            />
            <ActionButton
              action="create"
              label="Adicionar origem"
              size="md"
              mr={0}
              onClick={handleAdd}
              isLoading={isAdding}
            />
          </Flex>
        </FormField>

        <TableContainer mt={4}>
          <Table>
            <Thead>
              <Tr>
                <Th>Origem</Th>
                <Th w="1%">Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading ? (
                <TableSkeletonRows cols={2} rows={4} />
              ) : loadError ? (
                <TableErrorRow colSpan={2} entity="origens" feminine onRetry={loadSources} />
              ) : sources.length === 0 ? (
                <TableEmptyRow colSpan={2} entity="origem" feminine />
              ) : (
                sources.map((source) => (
                  <Tr key={source.id}>
                    <Td>{source.name}</Td>
                    <Td>
                      <ActionButton
                        action="delete"
                        mr={0}
                        onClick={() => {
                          setSourceToDelete(source)
                          deleteDialog.onOpen()
                        }}
                      />
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </FormModal>

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.onClose}
        onConfirm={handleDelete}
        action="delete"
        entity="origem"
        feminine
        name={sourceToDelete?.name}
        warning="Os leads já registrados com ela continuam com essa origem."
        irreversible={false}
      />
    </>
  )
}
